import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorCode, extractApiErrorMessage } from '@frontend/api/http';
import {
  ScopeBundle,
  ScopeBundleCreate,
  ScopeBundleReview,
  ScopeBundleUpdate,
} from '@frontend/types';

import {
  createScopeBundle,
  deleteScopeBundle,
  getAuthProviders,
  getScopeBundles,
  reviewScopeBundle,
  updateScopeBundle,
} from '../services';

export function useGetScopeBundles(providerId?: string) {
  const { data, isPending, error } = useQuery<ScopeBundle[]>({
    queryKey: getScopeBundles(providerId).key,
    queryFn: getScopeBundles(providerId).queryFn,
  });

  return { bundles: data ?? [], isPending, error };
}

export function useCreateScopeBundle(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: (payload: ScopeBundleCreate) => createScopeBundle(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scope-bundles'] });
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Scope bundle created successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorCode = extractApiErrorCode(error);
      if (errorCode === 'SCOPE_BUNDLE_NAME_EXISTS') {
        toaster.create('A scope bundle with this name already exists for this provider', {
          type: 'danger',
        });
      } else {
        const errorMessage = extractApiErrorMessage(error);
        toaster.create(errorMessage, { type: 'danger' });
      }
    },
  });

  return { createBundle: mutate, isCreating: isPending, error };
}

export function useUpdateScopeBundle(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ bundleId, payload }: { bundleId: string; payload: ScopeBundleUpdate }) =>
      updateScopeBundle(bundleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scope-bundles'] });
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Scope bundle updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorCode = extractApiErrorCode(error);
      if (errorCode === 'SCOPE_BUNDLE_NAME_EXISTS') {
        toaster.create('A scope bundle with this name already exists for this provider', {
          type: 'danger',
        });
      } else {
        const errorMessage = extractApiErrorMessage(error);
        toaster.create(errorMessage, { type: 'danger' });
      }
    },
  });

  return { updateBundle: mutate, isUpdating: isPending, error };
}

export function useDeleteScopeBundle() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: bundleId => deleteScopeBundle(bundleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scope-bundles'] });
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Scope bundle deleted successfully', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to delete scope bundle', { type: 'danger' });
    },
  });
}

export function useReviewScopeBundle(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ bundleId, payload }: { bundleId: string; payload: ScopeBundleReview }) =>
      reviewScopeBundle(bundleId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['scope-bundles'] });
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create(`Scope bundle ${variables.payload.status} successfully`, { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);
      toaster.create(errorMessage, { type: 'danger' });
    },
  });

  return { reviewBundle: mutate, isReviewing: isPending, error };
}
